import { useTheme } from "@/app/providers/ThemeContext";
import { encodeAttributionParam } from "@/shared/attribution";
import BrandLogo from "@/shared/components/BrandLogo";
import { getApiBaseUrl } from "@/shared/lib/apiBase";
import { gsap } from "gsap";
import { Moon, Shield, Sun, TrendingUp, Zap } from "lucide-react";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

const loginStyles = `
  @keyframes lh-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.3; transform: scale(0.7); }
  }
  .lh-pulse { animation: lh-pulse 2s infinite; }

  @keyframes dot-grid-drift {
    0%   { transform: translateY(0); }
    100% { transform: translateY(-44px); }
  }
  .dot-grid { animation: dot-grid-drift 8s linear infinite; }

  .login-page-grid {
    --login-bg: #f7f7f5;
    --login-fg: #111110;
    --login-muted: #6b6b68;
    --login-faint: #9e9c98;
    --login-accent: #00B8DB;
    --login-grid-dot: rgba(17,17,16,0.08);
    --login-glow-strong: rgba(0,184,219,0.10);
    --login-glow-soft: rgba(0,184,219,0.07);
    --login-card-bg: rgba(255,255,255,0.72);
    --login-card-border: rgba(17,17,16,0.10);
    --login-button-border: rgba(17,17,16,0.18);
    --login-button-text: rgba(17,17,16,0.62);
    --login-button-hover-border: rgba(0,184,219,0.45);
    --login-button-hover-bg: rgba(0,184,219,0.08);
    --login-button-hover-text: #111110;
    --login-pill-bg: rgba(255,255,255,0.58);
    --login-pill-border: rgba(17,17,16,0.10);
    --login-footer-link: rgba(17,17,16,0.34);
    --login-toggle-border: rgba(0,0,0,0.10);
    --login-toggle-bg: rgba(0,0,0,0.04);
    --login-toggle-bg-hover: rgba(0,0,0,0.08);
    --login-toggle-color: #111110;
    grid-template-columns: 1fr 1fr;
    min-height: 100vh;
    background: var(--login-bg);
    display: grid;
    position: relative;
    overflow: hidden;
    color: var(--login-fg);
    transition: background 0.25s ease, color 0.25s ease;
  }

  .login-page-grid[data-login-theme="dark"] {
    --login-bg: #0d0d0d;
    --login-fg: white;
    --login-muted: rgba(255,255,255,0.4);
    --login-faint: rgba(255,255,255,0.35);
    --login-grid-dot: rgba(255,255,255,0.07);
    --login-glow-strong: rgba(0,184,219,0.12);
    --login-glow-soft: rgba(0,184,219,0.07);
    --login-card-bg: rgba(255,255,255,0.04);
    --login-card-border: rgba(255,255,255,0.1);
    --login-button-border: rgba(255,255,255,0.2);
    --login-button-text: rgba(255,255,255,0.6);
    --login-button-hover-border: rgba(255,255,255,0.35);
    --login-button-hover-bg: rgba(255,255,255,0.04);
    --login-button-hover-text: white;
    --login-pill-bg: rgba(255,255,255,0.06);
    --login-pill-border: rgba(255,255,255,0.1);
    --login-footer-link: rgba(255,255,255,0.3);
    --login-toggle-border: rgba(255,255,255,0.1);
    --login-toggle-bg: rgba(255,255,255,0.05);
    --login-toggle-bg-hover: rgba(255,255,255,0.1);
    --login-toggle-color: #e0e0e0;
  }

  .login-theme-toggle {
    position: absolute;
    top: 24px;
    right: 24px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1px solid var(--login-toggle-border);
    background: var(--login-toggle-bg);
    color: var(--login-toggle-color);
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .login-theme-toggle:hover {
    background: var(--login-toggle-bg-hover);
    transform: translateY(-1px);
  }
  .login-theme-toggle:active { transform: scale(0.98); }

  .lh-input {
    width: 100%;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    padding: 12px 16px;
    font-family: var(--font-sans);
    font-size: 0.78rem;
    color: white;
    outline: none;
    transition: border-color 0.2s, background 0.2s;
    letter-spacing: 0.02em;
  }
  .lh-input::placeholder { color: rgba(255,255,255,0.22); }
  .lh-input:focus {
    border-color: #00B8DB;
    background: rgba(0,184,219,0.05);
  }

  .lh-submit {
    width: 100%;
    padding: 13px;
    border-radius: 100px;
    border: none;
    background: white;
    color: #0d0d0d;
    font-family: var(--font-sans);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .lh-submit:hover { background: #00B8DB; color: white; }
  .lh-submit:active { transform: scale(0.98); }

  .lh-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 20px 0;
  }
  .lh-divider::before,
  .lh-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255,255,255,0.08);
  }

  .lh-oauth {
    width: 100%;
    padding: 11px;
    border-radius: 100px;
    border: 1px solid var(--login-button-border);
    background: transparent;
    color: var(--login-button-text);
    font-family: var(--font-sans);
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    transition: all 0.2s;
    text-decoration: none;
  }
  .lh-oauth:hover {
    border-color: var(--login-button-hover-border);
    color: var(--login-button-hover-text);
    background: var(--login-button-hover-bg);
  }

  .pill-stat {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--login-pill-bg);
    border: 1px solid var(--login-pill-border);
    border-radius: 100px;
    padding: 6px 12px;
  }

  @media (max-width: 768px) {
    .login-page-grid {
      grid-template-columns: 1fr !important;
      overflow-y: auto;
    }
    .login-theme-toggle {
      top: 18px;
      right: 18px;
    }
    .login-left-panel {
      padding: 40px 24px 24px !important;
      text-align: center;
    }
    .login-left-panel .flex.flex-col.gap-3 {
      align-items: center;
    }
    .login-right-panel {
      padding: 0 24px 40px !important;
    }
    .login-footer-links {
      position: relative !important;
      bottom: auto !important;
      padding: 24px 0 !important;
    }
  }
`;

const stats = [
    { icon: TrendingUp, label: "Live Market Data" },
    { icon: Zap, label: "5-min Alerts" },
    { icon: Shield, label: "Secure & Private" },
];

export default function LoginPage() {
    const apiBaseUrl = getApiBaseUrl();
    const { theme, toggleTheme } = useTheme();
    const [searchParams] = useSearchParams();
    const mcpState = searchParams.get("mcp_state");
    // LH-114: forward captured UTM/creator attribution to OAuth start so the
    // API can bridge it through the provider redirect in a signed cookie.
    const attributionParam = encodeAttributionParam();
    const oauthQuery = [
        mcpState ? `mcp_state=${encodeURIComponent(mcpState)}` : "",
        attributionParam ? `attr=${attributionParam}` : "",
    ].filter(Boolean).join("&");
    const mcpSuffix = oauthQuery ? `?${oauthQuery}` : "";
    const pageRef = useRef(null);
    const leftRef = useRef(null);
    const cardRef = useRef(null);
    const statsRef = useRef([]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

            // Left panel: logo -> headline -> stats cascade
            tl.fromTo(leftRef.current?.querySelector(".lp-logo"),
                { opacity: 0, x: -20 },
                { opacity: 1, x: 0, duration: 0.55 }
            )
                .fromTo(leftRef.current?.querySelector(".lp-head"),
                    { opacity: 0, y: 30 },
                    { opacity: 1, y: 0, duration: 0.65 },
                    "-=0.3"
                )
                .fromTo(leftRef.current?.querySelector(".lp-sub"),
                    { opacity: 0, y: 16 },
                    { opacity: 1, y: 0, duration: 0.45 },
                    "-=0.35"
                )
                .fromTo(
                    statsRef.current.filter(Boolean),
                    { opacity: 0, x: -16 },
                    { opacity: 1, x: 0, duration: 0.4, stagger: 0.1 },
                    "-=0.25"
                );

            // Card: slide up from below with slight scale
            tl.fromTo(cardRef.current,
                { opacity: 0, y: 40, scale: 0.97 },
                { opacity: 1, y: 0, scale: 1, duration: 0.6 },
                "-=0.55"
            );

        }, pageRef);

        return () => ctx.revert();
    }, []);

    return (
        <>
            <style>{loginStyles}</style>
            <div
                ref={pageRef}
                className="login-page-grid"
                data-login-theme={theme}
            >
                <button
                    type="button"
                    aria-label="Toggle theme"
                    className="login-theme-toggle"
                    onClick={toggleTheme}
                >
                    {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                </button>

                {/* Dot grid background */}
                <div
                    className="dot-grid"
                    style={{
                        position: "absolute", inset: "-44px",
                        backgroundImage: "radial-gradient(circle, var(--login-grid-dot) 1px, transparent 1px)",
                        backgroundSize: "22px 22px",
                        pointerEvents: "none", zIndex: 0,
                    }}
                />

                {/* Glow */}
                <div style={{
                    position: "absolute", top: "-120px", left: "-120px",
                    width: "600px", height: "600px", pointerEvents: "none", zIndex: 0,
                    background: "radial-gradient(ellipse, var(--login-glow-strong) 0%, transparent 65%)",
                }} />
                <div style={{
                    position: "absolute", bottom: "-80px", right: "-80px",
                    width: "400px", height: "400px", pointerEvents: "none", zIndex: 0,
                    background: "radial-gradient(ellipse, var(--login-glow-soft) 0%, transparent 65%)",
                }} />

                {/* LEFT PANEL */}
                <div
                    ref={leftRef}
                    className="login-left-panel"
                    style={{
                        position: "relative", zIndex: 1,
                        display: "flex", flexDirection: "column",
                        justifyContent: "center", padding: "64px 56px",
                    }}
                >
                    {/* Logo */}
                    <a
                        href="/"
                        className="lp-logo"
                        style={{
                            display: "inline-flex", alignItems: "center", gap: "9px",
                            textDecoration: "none", marginBottom: "64px", opacity: 0,
                        }}
                    >
                        <BrandLogo size={32} textColor="var(--login-fg)" textSize="1.05rem" />
                    </a>

                    {/* Headline */}
                    <h1
                        className="lp-head"
                        style={{
                            fontFamily: "var(--font-serif)",
                            fontSize: "clamp(2rem, 3.2vw, 3rem)",
                            fontWeight: 700, color: "var(--login-fg)",
                            lineHeight: 1.1, letterSpacing: "-0.02em",
                            marginBottom: "18px", opacity: 0,
                        }}
                    >
                        Live Data.<br />Deep Analysis.<br />
                        <span style={{ color: "#00B8DB" }}>Real Decisions.</span>
                    </h1>

                    {/* Sub */}
                    <p
                        className="lp-sub"
                        style={{
                            fontSize: "0.87rem", color: "var(--login-muted)",
                            lineHeight: 1.75, fontStyle: "italic",
                            maxWidth: "380px", marginBottom: "40px", opacity: 0,
                        }}
                    >
                        LinkedHash combines live market intelligence with Claude to give you
                        decision-ready analysis - exactly when you need it.
                    </p>

                    {/* Stats */}
                    <div className="flex flex-col gap-3">
                        {stats.map(({ icon: Icon, label }, i) => (
                            <div
                                key={label}
                                ref={(el) => { statsRef.current[i] = el; }}
                                className="pill-stat"
                                style={{ opacity: 0, alignSelf: "flex-start" }}
                            >
                                <Icon size={13} color="#00B8DB" strokeWidth={2} />
                                <span style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "0.62rem", color: "var(--login-button-text)",
                                    letterSpacing: "0.06em", textTransform: "uppercase",
                                }}>
                                    {label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* RIGHT PANEL - Card */}
                <div className="login-right-panel" style={{
                    position: "relative", zIndex: 1,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "64px 56px",
                }}>
                    <div
                        ref={cardRef}
                        style={{
                            width: "100%", maxWidth: "400px",
                            background: "var(--login-card-bg)",
                            border: "1px solid var(--login-card-border)",
                            borderRadius: "20px", padding: "36px", opacity: 0,
                            backdropFilter: "blur(20px)",
                        }}
                    >
                        <div style={{ marginBottom: "28px" }}>
                            <h2 style={{
                                fontFamily: "var(--font-serif)",
                                fontSize: "1.4rem", fontWeight: 700, color: "var(--login-fg)",
                                letterSpacing: "-0.01em", marginBottom: "6px",
                            }}>Welcome to LinkedHash</h2>
                            <p style={{
                                fontSize: "0.78rem", color: "var(--login-faint)",
                                fontStyle: "italic", lineHeight: 1.5,
                            }}>Sign in to access your dashboard.</p>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <a href={`${apiBaseUrl}/auth/google/start${mcpSuffix}`} className="lh-oauth">
                                {/* Google */}
                                <svg width="15" height="15" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Continue with Google
                            </a>
                        </div>
                    </div>
                </div>

                {/* Footer / Legal Links */}
                <div className="login-footer-links" style={{
                    position: "absolute", bottom: 24, left: 0, right: 0,
                    textAlign: "center", zIndex: 1,
                }}>
                    <a href="/privacy" style={{ fontSize: "0.7rem", color: "var(--login-footer-link)", textDecoration: "none", marginRight: 16, padding: "12px 4px", display: "inline-block" }}>Privacy Policy</a>
                    <a href="/privacy" style={{ fontSize: "0.7rem", color: "var(--login-footer-link)", textDecoration: "none", padding: "12px 4px", display: "inline-block" }}>Terms of Service</a>
                </div>
            </div>
        </>
    );
}
